import { ComponentProps, useRef, RefObject } from 'react';
import { withStyles } from 'vopay';
import styles from './IconButton.module.scss';
import MaterialSymbol from '../../DataDisplay/MaterialSymbol/MaterialSymbol';
import { useDatadogLog } from '../logging';
import useTextColor from './useTextColor';

const cn = withStyles(styles);

export type ColorScheme = 'primary' | 'secondary' | 'destructive';
type Size = 'large' | 'standard' | 'small' | 'smallest';
type IconProps = ComponentProps<typeof MaterialSymbol>;

type IconButtonProps = React.ComponentProps<'button'> & {
  colorScheme?: ColorScheme;
  size?: Size;
  'aria-label': string;
  ref?: RefObject<HTMLButtonElement | null>;
} & (
    | {
        icon: IconProps['icon'];
        iconProps?: never;
      }
    | {
        iconProps: IconProps;
        icon?: never;
      }
  );

export default function IconButton({
  colorScheme = 'secondary',
  size = 'standard',
  className,
  style,
  icon,
  iconProps,
  'aria-label': ariaLabel,
  ref: forwardedRef,
  ...props
}: IconButtonProps) {
  const ref = useRef<HTMLButtonElement | null>(null);
  const log = useDatadogLog();

  const textColor = useTextColor({
    ref,
    colorScheme,
  });

  function combinedRef(node: HTMLButtonElement | null) {
    ref.current = node;

    if (typeof forwardedRef === 'function') {
      forwardedRef(node);
    } else if (forwardedRef && 'current' in forwardedRef) {
      forwardedRef.current = node;
    }
  }

  function handleClick(e: React.MouseEvent<HTMLButtonElement>) {
    log({
      message: `${ariaLabel} clicked`,
      payload: { name: ariaLabel },
      actionType: 'Button',
    });

    if (props.onClick) {
      props.onClick(e);
    }
  }

  return (
    <button
      ref={combinedRef}
      type="button"
      {...props}
      className={cn('iconButton', `color-${colorScheme}`, `size-${size}`, className)}
      style={{
        color: textColor,
        borderRadius: 'var(--buttonRadius)',
        ...style,
      }}
      onClick={handleClick}
      data-custom="true"
    >
      {iconProps && <MaterialSymbol {...iconProps} className={cn('icon', iconProps.className)} />}
      {icon && <MaterialSymbol className={cn('icon')} icon={icon} />}
    </button>
  );
}
